import React, { useState, useEffect } from 'react';

import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import { useModal } from '../../../Providers/ModalContext';
import { useNavMenu } from '../../../Providers/NavMenuContext';

import BackgroundNav from '../../../../Images/background/home_bg.png';
import BackgroundNavMob from '../../../../Images/background/home_bg-mob.png';
import BackgroundOrangeX from '../../../../Images/background/bg_studio.png';
import BackgroundBlueX from '../../../../Images/background/bg_films.png';
import LogoFilms from '../../../../Images/logos/logo_films_nav.png';
import LogoStudio from '../../../../Images/logos/logo_studio_nav.png';
import LogoKuter from '../../../../Images/logos/logo_kuter_xx.png';
import BgActiveMobile from '../../../../Images/background/bg_studio-mob.png';
import BgActiveMobile2 from '../../../../Images/background/bg_films-mob.png';

import Button from '../../../Global/Button';
import FullscreenMenu from './FullScreenMenu';

import { BTN_CONTACT, BTN_LINKS } from '../../../../utils/EN/Menu/index';

import { lockScroll, unlockScroll } from '../../../../utils/manager/scrollLock';

const LogoFilmsAnimation = keyframes`
  0% {
    filter: drop-shadow(0px 0px 0px rgba(147, 206, 255, 1));
  }
  50% {
    filter: drop-shadow(0px 0px 20px rgba(147, 206, 255, 1));
  }
  100% {
    filter: drop-shadow(0px 0px 0px rgba(147, 206, 255, 1));
  }
`;

const LogoStudioAnimation = keyframes`
  0% {
    filter: drop-shadow(0px 0px 0px rgba(250, 115, 19, 1));
  }
  50% {
    filter: drop-shadow(0px 0px 20px rgba(250, 115, 19, 1));
  }
  100% {
    filter: drop-shadow(0px 0px 0px rgba(250, 115, 19, 1));
  }
`;

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
  desktop: '1440px',
};

const Container = styled(motion.div)`
  width: 100vw;
  height: 100vh;
  position: relative;

  &:has(.level-campanas),
  &:has(.level-spots),
  &:has(.level-corporativo),
  &:has(.level-postProduction),
  &:has(.level-videoclips),
  &:has(.level-social),
  &:has(.level-fashion),
  &:has(.level-retrato),
  &:has(.level-sports),
  &:has(.level-drinks),
  &:has(.level-cultural),
  &:has(.level-photoCampanas) {
    .active-studio {
      display: none;
      opacity: 0;
    }
  }
`;

const ImgContainer = styled(motion.div)`
  position: absolute;
  inset: 0;
  background-image: url(${BackgroundNav});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top center;
  overflow: hidden;

  @media (max-width: ${breakpoints.mobile}) {
    background-image: url(${BackgroundNavMob});
    background-size: 100%;
  }
`;

const BgOrange = styled(motion.div)`
  display: none;
  position: absolute;
  inset: 0;
  background-image: url(${BackgroundOrangeX});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: right;
  overflow: hidden;
  width: 100%;

  @media (max-width: ${breakpoints.mobile}) {
    background-image: url(${BgActiveMobile});
    background-position: left;
  }
`;

const BgBlue = styled(motion.div)`
  display: none;
  position: absolute;
  inset: 0;
  background-image: url(${BackgroundBlueX});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: left;
  overflow: hidden;

  @media (max-width: ${breakpoints.mobile}) {
    background-image: url(${BgActiveMobile2});
    background-position: right;
  }
`;

const LogoContainer = styled.div`
  width: 100%;
  height: fit-content;
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: space-around;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column-reverse;
    justify-content: flex-end;
    height: 100%;
    margin-top: 25%;
    gap: 50%;
  }

  .films {
    img {
      animation: ${LogoFilmsAnimation} 2s ease-in infinite;
    }
    img:hover {
      filter: drop-shadow(0px 0px 20px rgba(147, 206, 255, 1));
      animation: none;
    }
  }

  .studio {
    img {
      animation: ${LogoStudioAnimation} 2s ease-in infinite;
    }
    img:hover {
      filter: drop-shadow(0px 0px 20px rgba(250, 115, 19, 1));
      animation: none;
    }
  }

  img {
    cursor: pointer;
    transition: all 0.3s ease-out;
  }
`;

const Logo = styled.img`
  max-width: 424px;
  width: 100%;
  display: block;
  height: auto;

  @media (max-width: ${breakpoints.mobile}) {
    max-width: 183px;
  }
`;

const ButtonContainer = styled(motion.div)`
  position: absolute;
  top: 40px;
  right: 40px;

  [class*='__MainButton'] {
    @media (max-width: ${breakpoints.mobile}) {
      font-size: 1.2rem;
      padding: 15px 20px;
    }
  }

  @media (max-width: ${breakpoints.mobile}) {
    top: 1%;
    right: 5%;
  }
`;

const MiddContainer = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  padding: 40px;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  flex-direction: column;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 20px;
  }

  .logo-kuter {
    z-index: 1;
    cursor: pointer;

    @media (max-width: ${breakpoints.mobile}) {
      position: absolute;
      bottom: 2%;
      right: 0;
      left: 0;
      margin: 0 auto;
      width: fit-content;
    }

    img {
      max-width: 159px;
      width: 100%;
      height: auto;
      display: block;
      transition: all 0.3s ease-out;
      margin: 0 auto;

      @media (max-width: ${breakpoints.mobile}) {
        max-width: 80px;
      }

      &:hover {
        filter: drop-shadow(0px 0px 14px rgba(255, 255, 255, 1))
          drop-shadow(0px 0px 2px rgba(255, 255, 255, 1));
        stroke: rgba(255, 255, 255, 1);
        stroke-width: 2px;
      }
    }
  }

  [class*='__MainButton'] {
    @media (max-width: ${breakpoints.mobile}) {
      position: absolute;
      left: 5%;
      top: 1%;

      font-size: 1.2rem;
      padding: 15px 20px;
    }
  }
`;

const LogoWrapper = styled(motion.div)`
  cursor: pointer;
`;

const Nav = () => {
  const { openModal } = useModal();
  const { activeMenu, setActiveMenu, menuStack, closeMenu } = useNavMenu();

  const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const media = window.matchMedia('(max-width: 600px)');
      setIsMobile(media.matches);

      const listener = () => setIsMobile(media.matches);
      media.addEventListener('change', listener);

      return () => media.removeEventListener('change', listener);
    }, []);

    return isMobile;
  };

  const isMobile = useIsMobile();

  useEffect(() => {
    if (activeMenu) {
      lockScroll();
    } else {
      unlockScroll();
    }
  }, [activeMenu]);

  const bgVariants = {
    initial: {
      x: 0,
      opacity: 0,
      ...(isMobile && { opacity: 0, display: 'none' }),
    },
    films: {
      x: '50%',
      opacity: 1,
      ...(isMobile && { opacity: 0, display: 'none' }),
    },
    studio: {
      x: '-50%',
      opacity: 1,
      ...(isMobile && { opacity: 0, display: 'none' }),
    },
  };

  const bgColor = {
    initial: { backgroundColor: 'transparent' },
    films: { backgroundColor: '#000' },
    studio: { backgroundColor: '#000' },
  };

  const fadeVariants = {
    visible: {
      opacity: 1,
      pointerEvents: 'auto',
      transition: { duration: 0.4 },
    },
    hidden: {
      opacity: 0,
      pointerEvents: 'none',
      transition: { duration: 0.3 },
    },
  };

  return (
    <Container
      variants={bgColor}
      animate={activeMenu || 'initial'}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
    >
      <ImgContainer
        variants={bgVariants}
        animate={activeMenu || 'initial'}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      />

      <BgOrange
        animate={{
          opacity: activeMenu === 'studio' ? 1 : 0,
          display: activeMenu === 'studio' ? 'block' : 'none',
          ...(isMobile && { x: activeMenu === 'studio' ? 0 : 100 }),
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      />

      <BgBlue
        animate={{
          opacity: activeMenu === 'films' ? 1 : 0,
          display: activeMenu === 'films' ? 'block' : 'none',
          ...(isMobile && { x: activeMenu === 'films' ? 0 : -100 }),
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      />

      <MiddContainer
        variants={fadeVariants}
        animate={activeMenu ? 'hidden' : 'visible'}
        transition={{ duration: 0.4 }}
      >
        <Button
          data-target="contact"
          onClick={(e) => openModal(e.currentTarget.dataset.target)}
          dataButton={BTN_CONTACT}
        />

        <a href="#info-kuter" className="logo-kuter">
          <img src={LogoKuter} alt="Logo Kuter XX" />
        </a>
      </MiddContainer>

      <ButtonContainer
        variants={fadeVariants}
        animate={activeMenu ? 'hidden' : 'visible'}
        transition={{ duration: 0.4 }}
      >
        <Button
          fontSize={'2rem'}
          padding={'17px 19px'}
          dataButton={BTN_LINKS}
        />
      </ButtonContainer>

      <LogoContainer className={`active-${activeMenu}`}>
        <LogoWrapper
          animate={{
            opacity:
              activeMenu === 'films' ? 0 : activeMenu === 'studio' ? 0 : 1,
            x:
              activeMenu === 'films'
                ? '205%'
                : activeMenu === 'studio'
                  ? -50
                  : 0,
            pointerEvents:
              activeMenu === 'films'
                ? 'all'
                : activeMenu === 'studio'
                  ? 'none'
                  : 'all',

            ...(isMobile && {
              x:
                activeMenu === 'films'
                  ? -50
                  : activeMenu === 'studio'
                    ? -50
                    : 0,
            }),
          }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          onClick={() => setActiveMenu('films')}
          className="films"
        >
          <Logo src={LogoFilms} alt="Kuter Films" />
        </LogoWrapper>

        <LogoWrapper
          animate={{
            opacity:
              activeMenu === 'studio' ? 0 : activeMenu === 'films' ? 0 : 1,
            x:
              activeMenu === 'studio'
                ? '-205%'
                : activeMenu === 'films'
                  ? 50
                  : 0,
            pointerEvents:
              activeMenu === 'studio'
                ? 'all'
                : activeMenu === 'films'
                  ? 'none'
                  : 'all',

            ...(isMobile && {
              x: activeMenu === 'studio' ? 50 : activeMenu === 'films' ? 50 : 0,
            }),
          }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          onClick={() => setActiveMenu('studio')}
          className="studio"
        >
          <Logo src={LogoStudio} alt="Kuter Studio" />
        </LogoWrapper>
      </LogoContainer>

      <AnimatePresence>
        {activeMenu && (
          <FullscreenMenu
            key={activeMenu}
            type={activeMenu}
            initialStack={menuStack}
            onClose={closeMenu}
          />
        )}
      </AnimatePresence>
    </Container>
  );
};

export default Nav;
